import React, { useState, useRef } from 'react'
import { Input, Button, Spin, Modal, Tag } from 'antd'
import { toast } from 'react-toastify'
import {
  getAxiomaRagSearch,
  getRelacionesSugeridasPorCodigo,
  postBuscarSimilaresReemplazantes,
  postRepuestosPorCodigos,
  putRelacionReemplazante,
  putDesvincularReemplazante,
  putRelacionHomologo,
  putDesvincularHomologo,
} from 'src/services/axiomaService'
import {
  TinderVsComparison,
  MatchCelebration,
} from '../../components/Axioma/FormSections/BuscarReemplazantesSection'
import { mapRagResultToProduct } from './reemplazantesConstants'
import {
  AddMaterialModal,
  ComparisonOverlay,
  ProductCardsGrid,
} from './reemplazantesComponents'

// Los datos se manejan igual que "Buscar similares" del formulario
// (BuscarReemplazantesSection): candidatos por /v1/hu/similares y detalle por
// /v1/repuestos/por_codigos en lotes de 5 (mismo BATCH_SIZE del modal: con
// lotes más grandes el backend no firma las imágenes); los reemplazantes ya
// vinculados vienen de /v1/repuestos/relaciones_sugerida_codigo. Solo cambia
// la presentación: la grilla de cards del mock (referencia de primeras) y,
// al abrir una card, la misma comparación del modal del formulario.
const DETALLE_CHUNK = 5
// Los candidatos se cargan paginados de a 10 con el botón "Ver más"
const PAGE_SIZE = 10

const normalizarCodigo = (codigo) =>
  String(codigo ?? '').replace(/\.(jpg|jpeg|png|webp)$/i, '')

const formatSimilitud = (similitud) => {
  const valor = Number(similitud)
  if (Number.isNaN(valor)) return '—'
  return `${Math.round(valor * 100)}%`
}

// Mismo parseo de /repuestos/por_codigos que usa el modal del formulario.
const parseResultadosPorCodigos = (data) => {
  const map = new Map()
  if (!data) return map

  if (Array.isArray(data.resultados)) {
    data.resultados.forEach((item) => {
      const codigo = normalizarCodigo(
        item.codigo || item.repuestos?.[0]?.repuesto_codigo_material_sap,
      )
      if (!codigo) return
      map.set(codigo, {
        repuesto: item.repuestos?.[0] || null,
        imagenes: Array.isArray(item.imagenes) ? item.imagenes : [],
      })
    })
    return map
  }

  if (Array.isArray(data.repuestos)) {
    const repuesto = data.repuestos[0]
    const codigo = normalizarCodigo(repuesto?.repuesto_codigo_material_sap)
    if (codigo) {
      map.set(codigo, {
        repuesto,
        imagenes: Array.isArray(data.imagenes) ? data.imagenes : [],
      })
    }
  }

  return map
}

const RAG_CONCURRENCY = 3

// Una columna de Excel llega como líneas (o tabs si copian varias columnas).
const parseTermsFromExcel = (raw) => {
  const seen = new Set()
  const terms = []
  String(raw ?? '')
    .split(/[\n\r\t]+/)
    .forEach((part) => {
      const term = part.trim()
      if (!term) return
      const key = term.toUpperCase()
      if (seen.has(key)) return
      seen.add(key)
      terms.push(term)
    })
  return terms
}

const mergeTerms = (current, incoming) => {
  const seen = new Set(current.map((t) => t.toUpperCase()))
  const merged = [...current]
  incoming.forEach((term) => {
    const key = term.toUpperCase()
    if (seen.has(key)) return
    seen.add(key)
    merged.push(term)
  })
  return merged
}

const buscarTerminoRag = async (term) => {
  const data = await getAxiomaRagSearch(term)
  const rawResults = data?.data?.results
  if (!Array.isArray(rawResults)) {
    return { ok: false, products: [] }
  }
  return {
    ok: true,
    products: rawResults
      .map(mapRagResultToProduct)
      .filter((product) => product.id != null),
  }
}

// Convierte un repuesto (con su detalle) al formato de material de las cards.
// Conserva repuesto/imagenes/similitud crudos para la comparación completa.
const buildProductFromDetalle = (codigo, data, similitud) => {
  const repuesto = data?.repuesto || null
  const imagenes = Array.isArray(data?.imagenes) ? data.imagenes : []
  const similitudNum = Number(similitud)

  return {
    id: repuesto?.repuesto_id ?? codigo,
    sku: repuesto?.repuesto_codigo_material_sap || codigo,
    nombre: repuesto?.repuesto_descripcion || `Material ${codigo}`,
    marca: repuesto?.marca_nombre || '—',
    categoria: repuesto?.categoria_nombre || '—',
    subcategoria: repuesto?.subcategoria_nombre || '—',
    costo: '—',
    imagen: imagenes[0]?.signed_url || '',
    proveedor: repuesto?.repuesto_fabricante || '—',
    medidas: null,
    similarityPct: Number.isFinite(similitudNum)
      ? Math.round(similitudNum * 100)
      : null,
    similitud,
    imagenes,
    repuesto,
  }
}

const BuscarPorTextoTab = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [terms, setTerms] = useState([])
  const [activeTerm, setActiveTerm] = useState(null)
  const [resultsByTerm, setResultsByTerm] = useState({})
  const [termStatus, setTermStatus] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedReferenceId, setSelectedReferenceId] = useState(null)
  const [selectedReplacementId, setSelectedReplacementId] = useState(null)
  const searchGenRef = useRef(0)
  const resultsByTermRef = useRef({})
  const workspaceRef = useRef({})
  const termsRef = useRef([])

  // Datos reales de la referencia seleccionada
  const [codigoSeleccionado, setCodigoSeleccionado] = useState(null)
  const [referenciaDetalle, setReferenciaDetalle] = useState(null)
  const [candidatosRaw, setCandidatosRaw] = useState([])
  const [candidatoProducts, setCandidatoProducts] = useState([])
  const [loadedCount, setLoadedCount] = useState(0)
  const [reemplazantes, setReemplazantes] = useState([])
  const [sustitutos, setSustitutos] = useState([])
  const [loadingCandidatos, setLoadingCandidatos] = useState(false)
  const [loadingMas, setLoadingMas] = useState(false)
  const [accionEnCurso, setAccionEnCurso] = useState(null)
  const [celebracion, setCelebracion] = useState(null)

  // Conteo de matches reales por referencia (para el badge de la lista)
  const [matchCountByReference, setMatchCountByReference] = useState({})

  // Materiales agregados a mano (flujo local de "Agregar material")
  const [customProductsByReference, setCustomProductsByReference] = useState(
    {},
  )
  const [matchedCustomByReference, setMatchedCustomByReference] = useState({})
  const [addMaterialModalOpen, setAddMaterialModalOpen] = useState(false)

  const captureWorkspace = () => ({
    selectedReferenceId,
    codigoSeleccionado,
    referenciaDetalle,
    candidatosRaw,
    candidatoProducts,
    loadedCount,
    reemplazantes,
    sustitutos,
  })

  const restoreWorkspace = (workspace) => {
    setSelectedReplacementId(null)
    setCelebracion(null)
    setAddMaterialModalOpen(false)
    if (!workspace) {
      setSelectedReferenceId(null)
      setCodigoSeleccionado(null)
      setReferenciaDetalle(null)
      setCandidatosRaw([])
      setCandidatoProducts([])
      setLoadedCount(0)
      setReemplazantes([])
      setSustitutos([])
      return
    }
    setSelectedReferenceId(workspace.selectedReferenceId)
    setCodigoSeleccionado(workspace.codigoSeleccionado)
    setReferenciaDetalle(workspace.referenciaDetalle)
    setCandidatosRaw(workspace.candidatosRaw)
    setCandidatoProducts(workspace.candidatoProducts)
    setLoadedCount(workspace.loadedCount)
    setReemplazantes(workspace.reemplazantes)
    setSustitutos(workspace.sustitutos)
  }

  const selectTerm = (term) => {
    if (!term || term === activeTerm) return
    if (activeTerm) {
      workspaceRef.current[activeTerm] = captureWorkspace()
    }
    setActiveTerm(term)
    restoreWorkspace(workspaceRef.current[term])
  }

  const handleClearAll = () => {
    searchGenRef.current += 1
    setSearchTerm('')
    termsRef.current = []
    setTerms([])
    setActiveTerm(null)
    setResultsByTerm({})
    resultsByTermRef.current = {}
    setTermStatus({})
    setError(null)
    setLoading(false)
    workspaceRef.current = {}
    setMatchCountByReference({})
    setCustomProductsByReference({})
    setMatchedCustomByReference({})
    restoreWorkspace(null)
  }

  const handleSearch = async (rawOverride) => {
    const parsed = parseTermsFromExcel(rawOverride ?? searchTerm)
    if (!parsed.length) return

    const gen = ++searchGenRef.current
    const merged = mergeTerms(termsRef.current, parsed)
    const toFetch = merged.filter(
      (term) => resultsByTermRef.current[term] === undefined,
    )

    termsRef.current = merged
    setTerms(merged)
    setSearchTerm('')
    setError(null)
    setActiveTerm((prev) =>
      prev && merged.includes(prev) ? prev : merged[0],
    )

    if (!toFetch.length) {
      if (!activeTerm && merged[0]) setActiveTerm(merged[0])
      return
    }

    setLoading(true)
    setTermStatus((prev) => {
      const next = { ...prev }
      toFetch.forEach((term) => {
        next[term] = 'loading'
      })
      return next
    })

    let cursor = 0
    const worker = async () => {
      while (cursor < toFetch.length) {
        const term = toFetch[cursor]
        cursor += 1
        if (searchGenRef.current !== gen) return
        const { ok, products } = await buscarTerminoRag(term)
        if (searchGenRef.current !== gen) return
        resultsByTermRef.current = {
          ...resultsByTermRef.current,
          [term]: products,
        }
        setResultsByTerm((prev) => ({ ...prev, [term]: products }))
        setTermStatus((prev) => ({
          ...prev,
          [term]: !ok ? 'error' : products.length ? 'done' : 'empty',
        }))
      }
    }

    try {
      const workers = Math.min(RAG_CONCURRENCY, toFetch.length)
      await Promise.all(Array.from({ length: workers }, () => worker()))
    } catch {
      if (searchGenRef.current === gen) {
        setError('No se pudo completar la búsqueda. Intenta de nuevo.')
      }
    } finally {
      if (searchGenRef.current === gen) setLoading(false)
    }
  }

  const handlePaste = (event) => {
    const text = event.clipboardData?.getData('text/plain') || ''
    const parsed = parseTermsFromExcel(text)
    if (parsed.length < 2) return
    event.preventDefault()
    handleSearch(text)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSearch()
    }
  }

  const handleRemoveTerm = (term) => {
    const next = terms.filter((item) => item !== term)
    termsRef.current = next
    setTerms(next)
    if (activeTerm !== term) return
    const fallback = next[0] || null
    setActiveTerm(fallback)
    restoreWorkspace(fallback ? workspaceRef.current[fallback] : null)
  }

  // Trae el detalle de una página de candidatos, en lotes de 5 en paralelo
  // (mismo tamaño de lote del modal: con lotes más grandes no llegan imágenes)
  const cargarPaginaCandidatos = async (lista, desde, codigoReferencia) => {
    const pagina = lista.slice(desde, desde + PAGE_SIZE)
    if (!pagina.length) return []

    const codigosPagina = pagina
      .map((c) => normalizarCodigo(c.codigo))
      .filter(Boolean)
    const grupos = []
    for (let i = 0; i < codigosPagina.length; i += DETALLE_CHUNK) {
      grupos.push(codigosPagina.slice(i, i + DETALLE_CHUNK))
    }
    const mapas = await Promise.all(
      grupos.map((grupo) =>
        postRepuestosPorCodigos(grupo).then(parseResultadosPorCodigos),
      ),
    )
    const resultadosMap = new Map()
    mapas.forEach((m) => m.forEach((v, k) => resultadosMap.set(k, v)))

    return pagina
      .map((c) => {
        const codigoCandidato = normalizarCodigo(c.codigo)
        return buildProductFromDetalle(
          codigoCandidato,
          resultadosMap.get(codigoCandidato),
          c.similitud,
        )
      })
      .filter((product) => product.sku !== codigoReferencia)
  }

  const handleResultClick = async (item) => {
    const codigo = normalizarCodigo(item.sku || item.id)
    if (!codigo || loadingCandidatos) return

    setSelectedReferenceId(item.id)
    setSelectedReplacementId(null)
    setAddMaterialModalOpen(false)
    setCodigoSeleccionado(codigo)
    setReferenciaDetalle(null)
    setCandidatosRaw([])
    setCandidatoProducts([])
    setLoadedCount(0)
    setReemplazantes([])
    setSustitutos([])
    setLoadingCandidatos(true)

    try {
      // Mismos endpoints que el botón "Buscar similares" del formulario; la
      // referencia va en su propia petición, igual que cargarMaterialOriginal
      const [relacionesResponse, similaresResponse, referenciaMap] =
        await Promise.all([
          getRelacionesSugeridasPorCodigo(codigo),
          postBuscarSimilaresReemplazantes(codigo, 0.8, false),
          postRepuestosPorCodigos([codigo]).then(parseResultadosPorCodigos),
        ])

      const relaciones = relacionesResponse?.relaciones
      const reemplazantesList = relaciones?.reemplazantes_sugeridos ?? []
      setReemplazantes(reemplazantesList)
      setSustitutos(relaciones?.homologos_sugeridos ?? [])
      setMatchCountByReference((prev) => ({
        ...prev,
        [item.id]: reemplazantesList.length,
      }))

      setReferenciaDetalle(referenciaMap.get(codigo) ?? null)

      const candidatos = Array.isArray(similaresResponse?.candidatos)
        ? similaresResponse.candidatos
        : []
      setCandidatosRaw(candidatos)

      // Primera página de candidatos (de a 10, con "Ver más" para el resto)
      const primeraPagina = await cargarPaginaCandidatos(candidatos, 0, codigo)
      setCandidatoProducts(primeraPagina)
      setLoadedCount(Math.min(PAGE_SIZE, candidatos.length))

      if (!candidatos.length) {
        toast.info('No se encontraron candidatos reemplazantes')
      }
    } catch {
      toast.error('Error al buscar reemplazantes')
    } finally {
      setLoadingCandidatos(false)
    }
  }

  const handleVerMas = async () => {
    if (loadingMas || loadingCandidatos || loadedCount >= candidatosRaw.length)
      return

    setLoadingMas(true)
    try {
      const siguientePagina = await cargarPaginaCandidatos(
        candidatosRaw,
        loadedCount,
        codigoSeleccionado,
      )
      setCandidatoProducts((prev) => {
        const idsExistentes = new Set(prev.map((product) => product.id))
        return [
          ...prev,
          ...siguientePagina.filter(
            (product) => !idsExistentes.has(product.id),
          ),
        ]
      })
      setLoadedCount((prev) =>
        Math.min(prev + PAGE_SIZE, candidatosRaw.length),
      )
    } catch {
      toast.error('Error al cargar más candidatos')
    } finally {
      setLoadingMas(false)
    }
  }

  const results = activeTerm ? resultsByTerm[activeTerm] ?? null : null
  const activeStatus = activeTerm ? termStatus[activeTerm] : null
  const loadedTermsCount = terms.filter(
    (term) => termStatus[term] && termStatus[term] !== 'loading',
  ).length

  const referenceItem =
    results?.find((product) => product.id === selectedReferenceId) ?? null

  // Card de referencia: datos reales del repuesto con fallback al resultado RAG
  const referenceProduct = referenceItem
    ? {
        ...referenceItem,
        nombre:
          referenciaDetalle?.repuesto?.repuesto_descripcion ||
          referenceItem.nombre,
        marca: referenciaDetalle?.repuesto?.marca_nombre || referenceItem.marca,
        categoria:
          referenciaDetalle?.repuesto?.categoria_nombre ||
          referenceItem.categoria,
        imagen:
          referenciaDetalle?.imagenes?.[0]?.signed_url || referenceItem.imagen,
      }
    : null

  const customProducts = selectedReferenceId
    ? customProductsByReference[selectedReferenceId] ?? []
    : []

  const replacementProducts = referenceProduct
    ? [...candidatoProducts, ...customProducts]
    : []

  const selectedProduct = replacementProducts.find(
    (product) => product.id === selectedReplacementId,
  )
  const esSeleccionCustom = Boolean(selectedProduct?.isCustom)

  // Matches reales (relaciones vinculadas) + matches locales de agregados a mano
  const matchedCustomIds = selectedReferenceId
    ? matchedCustomByReference[selectedReferenceId] ?? []
    : []

  const matchedIds = [
    ...candidatoProducts
      .filter((product) =>
        reemplazantes.some(
          (r) => r.repuesto_id === product.repuesto?.repuesto_id,
        ),
      )
      .map((product) => product.id),
    ...matchedCustomIds,
  ]

  const isSelectedProductMatched =
    selectedProduct && matchedIds.includes(selectedProduct.id)

  const ajustarConteoMatches = (delta) => {
    setMatchCountByReference((prev) => ({
      ...prev,
      [selectedReferenceId]: Math.max(
        0,
        (prev[selectedReferenceId] ?? 0) + delta,
      ),
    }))
  }

  // ==================== Comparación (misma del modal del formulario) ====================

  const repuestoReferencia = referenciaDetalle?.repuesto ?? null
  const revisionId = repuestoReferencia?.revision_id
  const codigoOriginal =
    repuestoReferencia?.repuesto_codigo_material_sap || codigoSeleccionado

  const candidatoRepuestoId = esSeleccionCustom
    ? null
    : selectedProduct?.repuesto?.repuesto_id
  const yaEsReemplazante = candidatoRepuestoId
    ? reemplazantes.some((r) => r.repuesto_id === candidatoRepuestoId)
    : false
  const yaEsSustituto = candidatoRepuestoId
    ? sustitutos.some((s) => s.repuesto_id === candidatoRepuestoId)
    : false
  const esMismoMaterial =
    candidatoRepuestoId != null &&
    repuestoReferencia?.repuesto_id != null &&
    candidatoRepuestoId === repuestoReferencia.repuesto_id

  const baseAccion = Boolean(candidatoRepuestoId)
  const puedeMatchReemplazante =
    baseAccion && !esMismoMaterial && !yaEsReemplazante
  const puedeMatchSustituto = baseAccion && !esMismoMaterial && !yaEsSustituto
  const puedeQuitarReemplazante = baseAccion && yaEsReemplazante
  const puedeQuitarSustituto = baseAccion && yaEsSustituto

  const dispararCelebracion = (tipoLabel) => {
    setCelebracion({
      imagenIzq: referenciaDetalle?.imagenes?.[0]?.signed_url || null,
      imagenDer: selectedProduct?.imagenes?.[0]?.signed_url || null,
      codigoIzq: codigoOriginal,
      codigoDer: selectedProduct?.sku,
      similitud: formatSimilitud(selectedProduct?.similitud),
      tipo: tipoLabel,
    })
    setTimeout(() => setCelebracion(null), 1900)
  }

  const construirRelacion = (repuesto) => ({
    repuesto_id: repuesto.repuesto_id,
    codigo_material_sap: repuesto.repuesto_codigo_material_sap,
    npf: repuesto.repuesto_npf,
    descripcion: repuesto.repuesto_descripcion,
    fabricante: repuesto.repuesto_fabricante,
    origen: 'MOMENTOS_HU',
  })

  const validarAccion = () => {
    if (!selectedProduct?.repuesto?.repuesto_id) {
      toast.error('El candidato no tiene un repuesto válido')
      return false
    }
    if (!revisionId) {
      toast.error('El material no tiene una revisión para vincular relaciones')
      return false
    }
    return true
  }

  const handleMatchReemplazante = async () => {
    if (!puedeMatchReemplazante || accionEnCurso || !validarAccion()) return
    const { repuesto } = selectedProduct
    setAccionEnCurso('match-reempl')
    try {
      const response = await putRelacionReemplazante({
        revisionId,
        reemplazanteId: repuesto.repuesto_id,
        origen: 'MOMENTOS_HU',
      })
      if (response && response.success) {
        setReemplazantes((prev) => [...prev, construirRelacion(repuesto)])
        ajustarConteoMatches(1)
        dispararCelebracion('Reemplazante')
        toast.success('Reemplazante agregado exitosamente')
      } else {
        toast.error(response?.message || 'Error al agregar el reemplazante')
      }
    } finally {
      setAccionEnCurso(null)
    }
  }

  const handleQuitarReemplazante = async () => {
    if (!puedeQuitarReemplazante || accionEnCurso || !validarAccion()) return
    const { repuesto } = selectedProduct
    setAccionEnCurso('unmatch-reempl')
    try {
      const response = await putDesvincularReemplazante({
        revisionId,
        reemplazanteId: repuesto.repuesto_id,
      })
      if (response && response.success) {
        setReemplazantes((prev) =>
          prev.filter((r) => r.repuesto_id !== repuesto.repuesto_id),
        )
        ajustarConteoMatches(-1)
        toast.success('Reemplazante desvinculado exitosamente')
      } else {
        toast.error(
          response?.message || 'Error al desvincular el reemplazante',
        )
      }
    } finally {
      setAccionEnCurso(null)
    }
  }

  const handleMatchSustituto = async () => {
    if (!puedeMatchSustituto || accionEnCurso || !validarAccion()) return
    const { repuesto } = selectedProduct
    setAccionEnCurso('match-sust')
    try {
      const response = await putRelacionHomologo({
        revisionId,
        homologoId: repuesto.repuesto_id,
        origen: 'MOMENTOS_HU',
      })
      if (response && response.success) {
        setSustitutos((prev) => [...prev, construirRelacion(repuesto)])
        dispararCelebracion('Sustituto')
        toast.success('Sustituto agregado exitosamente')
      } else {
        toast.error(response?.message || 'Error al agregar el sustituto')
      }
    } finally {
      setAccionEnCurso(null)
    }
  }

  const handleQuitarSustituto = async () => {
    if (!puedeQuitarSustituto || accionEnCurso || !validarAccion()) return
    const { repuesto } = selectedProduct
    setAccionEnCurso('unmatch-sust')
    try {
      const response = await putDesvincularHomologo({
        revisionId,
        homologoId: repuesto.repuesto_id,
      })
      if (response && response.success) {
        setSustitutos((prev) =>
          prev.filter((s) => s.repuesto_id !== repuesto.repuesto_id),
        )
        toast.success('Sustituto desvinculado exitosamente')
      } else {
        toast.error(response?.message || 'Error al desvincular el sustituto')
      }
    } finally {
      setAccionEnCurso(null)
    }
  }

  // Match local para materiales agregados a mano (sin vínculo en el backend)
  const handleMatchToggleCustom = () => {
    if (!selectedReferenceId || !selectedProduct) return
    setMatchedCustomByReference((prev) => {
      const current = prev[selectedReferenceId] ?? []
      return {
        ...prev,
        [selectedReferenceId]: current.includes(selectedProduct.id)
          ? current.filter((id) => id !== selectedProduct.id)
          : [...current, selectedProduct.id],
      }
    })
    setSelectedReplacementId(null)
  }

  const handleDeleteCustomProduct = (productId) => {
    if (!selectedReferenceId || matchedIds.includes(productId)) return

    setCustomProductsByReference((prev) => ({
      ...prev,
      [selectedReferenceId]: (prev[selectedReferenceId] ?? []).filter(
        (product) => product.id !== productId,
      ),
    }))

    if (selectedReplacementId === productId) {
      setSelectedReplacementId(null)
    }
  }

  const cerrarComparacion = () => {
    setSelectedReplacementId(null)
    setCelebracion(null)
  }

  return (
    <>
      <div className="reemplazantes-search-bar reemplazantes-search-bar--batch">
        <div className="reemplazantes-search-composer">
          <Input.TextArea
            className="reemplazantes-search-input reemplazantes-search-textarea"
            placeholder="Escribe un modelo o pega una columna de Excel (un modelo por línea)"
            value={searchTerm}
            autoSize={{ minRows: 1, maxRows: 6 }}
            onChange={(event) => setSearchTerm(event.target.value)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
          />
          <p className="reemplazantes-search-hint">
            Pega varias celdas de Excel: cada fila entra a la cola. Si abres un
            material y vuelves, la búsqueda se queda.
          </p>
          {terms.length > 0 && (
            <div className="reemplazantes-search-chips">
              <span className="reemplazantes-search-chips-meta">
                {loading
                  ? `Buscando ${loadedTermsCount} de ${terms.length}…`
                  : `${terms.length} modelo${
                      terms.length === 1 ? '' : 's'
                    } en la cola`}
              </span>
              <div className="reemplazantes-search-chips-list">
                {terms.map((term) => {
                  const count = resultsByTerm[term]?.length
                  const status = termStatus[term]
                  return (
                    <Tag
                      key={term}
                      closable
                      onClose={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        handleRemoveTerm(term)
                      }}
                      onClick={() => selectTerm(term)}
                      className={`reemplazantes-search-chip${
                        term === activeTerm ? ' is-active' : ''
                      }`}
                    >
                      {term}
                      {status === 'loading' ? '…' : ''}
                      {typeof count === 'number' ? ` · ${count}` : ''}
                    </Tag>
                  )
                })}
              </div>
            </div>
          )}
        </div>
        <div className="reemplazantes-search-actions">
          <Button
            type="primary"
            onClick={() => handleSearch()}
            loading={loading}
          >
            Buscar
          </Button>
          {terms.length > 0 && (
            <Button onClick={handleClearAll}>Limpiar</Button>
          )}
        </div>
      </div>

      <div className="reemplazantes-content">
        <div className="reemplazantes-results-panel">
          {terms.length === 0 && !loading && !error && (
            <p className="reemplazantes-empty-message">
              Escribe un modelo o pega una columna de Excel y presiona Buscar.
            </p>
          )}

          {error && (
            <p className="reemplazantes-empty-message">{error}</p>
          )}

          {terms.length > 0 && (
            <>
              {terms.length > 1 && activeTerm && (
                <p className="reemplazantes-results-heading">
                  Materiales de “{activeTerm}”
                </p>
              )}

              {activeStatus === 'loading' && (
                <div className="reemplazantes-loading">
                  <Spin tip={`Buscando ${activeTerm}...`} />
                </div>
              )}

              {activeStatus === 'error' && (
                <p className="reemplazantes-empty-message">
                  No se pudo buscar “{activeTerm}”.
                </p>
              )}

              {activeStatus === 'empty' && (
                <p className="reemplazantes-empty-message">
                  No hay resultados para “{activeTerm}”.
                </p>
              )}

              {Array.isArray(results) && results.length > 0 && (
                <div className="reemplazantes-results-list">
                  {results.map((item) => {
                    const itemMatchCount =
                      (matchCountByReference[item.id] ?? 0) +
                      (matchedCustomByReference[item.id]?.length ?? 0)
                    const itemLabel = item.sku
                      ? `${item.sku} · ${item.nombre}`
                      : item.nombre

                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`reemplazantes-result-button${
                          selectedReferenceId === item.id
                            ? ' reemplazantes-result-button--active'
                            : ''
                        }${
                          itemMatchCount > 0
                            ? ' reemplazantes-result-button--has-match'
                            : ''
                        }`}
                        onClick={() => handleResultClick(item)}
                        disabled={loadingCandidatos}
                      >
                        <span className="reemplazantes-result-button-label">
                          {itemLabel}
                        </span>
                        {itemMatchCount > 0 && (
                          <span className="reemplazantes-result-button-match">
                            {itemMatchCount} match
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {referenceProduct && (
          <div className="reemplazantes-cards-panel">
            <div className="reemplazantes-cards-toolbar">
              <Button
                type="default"
                onClick={() => setAddMaterialModalOpen(true)}
              >
                Agregar material
              </Button>
            </div>

            {loadingCandidatos ? (
              <div className="reemplazantes-loading">
                <Spin tip="Buscando candidatos..." />
              </div>
            ) : replacementProducts.length === 0 ? (
              <p className="reemplazantes-empty-message">
                No hay candidatos reemplazantes para esta referencia.
              </p>
            ) : (
              <>
                {candidatosRaw.length > 0 && (
                  <p
                    style={{
                      margin: '0 0 8px',
                      color: '#64748b',
                      fontSize: 13,
                    }}
                  >
                    Mostrando {Math.min(loadedCount, candidatosRaw.length)} de{' '}
                    {candidatosRaw.length} candidatos
                  </p>
                )}
                <ProductCardsGrid
                  products={replacementProducts}
                  referenceProduct={referenceProduct}
                  selectedIds={selectedReplacementId}
                  matchedIds={matchedIds}
                  onCardClick={(product) =>
                    setSelectedReplacementId(product.id)
                  }
                  onDeleteCustom={handleDeleteCustomProduct}
                  dimmed={Boolean(selectedProduct)}
                />
                {loadedCount < candidatosRaw.length && (
                  <div style={{ marginTop: 12 }}>
                    <Button block onClick={handleVerMas} loading={loadingMas}>
                      Ver más
                    </Button>
                  </div>
                )}
              </>
            )}

            <AddMaterialModal
              open={addMaterialModalOpen}
              customProducts={customProducts}
              onClose={() => setAddMaterialModalOpen(false)}
              onConfirm={(product) => {
                setCustomProductsByReference((prev) => ({
                  ...prev,
                  [selectedReferenceId]: [
                    ...(prev[selectedReferenceId] ?? []),
                    product,
                  ],
                }))
              }}
            />

            {/* Materiales agregados a mano: comparación local del mock */}
            {selectedProduct && esSeleccionCustom && (
              <ComparisonOverlay
                leftProduct={referenceProduct}
                rightProduct={selectedProduct}
                leftTitle="Referencia"
                rightTitle="Seleccionado"
                leftBadge="Referencia"
                rightBadge="Seleccionado"
                isMatched={isSelectedProductMatched}
                matchLabel="Match"
                onClose={() => setSelectedReplacementId(null)}
                onMatchToggle={handleMatchToggleCustom}
              />
            )}
          </div>
        )}
      </div>

      {/* Candidatos reales: misma comparación del modal del formulario
          (imágenes en paralelo, plantillas, reemplazante/sustituto) */}
      <Modal
        title="Comparación de materiales"
        open={Boolean(selectedProduct && !esSeleccionCustom)}
        onCancel={cerrarComparacion}
        footer={null}
        width={920}
        destroyOnClose
        className="buscar-reemplazantes-modal buscar-reemplazantes-modal--vs"
        centered
        style={{ maxWidth: 'calc(100vw - 300px)' }}
        styles={{
          body: {
            padding: 0,
            maxHeight: '88vh',
            overflow: 'hidden',
          },
        }}
      >
        {celebracion && <MatchCelebration {...celebracion} />}
        {selectedProduct && !esSeleccionCustom && (
          <TinderVsComparison
            materialOriginal={
              referenciaDetalle ?? { repuesto: null, imagenes: [] }
            }
            comparacionActiva={{
              similitud: selectedProduct.similitud,
              codigo: selectedProduct.sku,
              repuesto: selectedProduct.repuesto,
              imagenes: selectedProduct.imagenes,
            }}
            imagenesMaterial={[]}
            codigoOriginal={codigoOriginal}
            yaEsReemplazante={yaEsReemplazante}
            yaEsSustituto={yaEsSustituto}
            esMismoMaterial={esMismoMaterial}
            puedeMatchReemplazante={puedeMatchReemplazante}
            puedeMatchSustituto={puedeMatchSustituto}
            puedeQuitarReemplazante={puedeQuitarReemplazante}
            puedeQuitarSustituto={puedeQuitarSustituto}
            accionEnCurso={accionEnCurso}
            onVolverCandidatos={cerrarComparacion}
            onMatchReemplazante={handleMatchReemplazante}
            onQuitarReemplazante={handleQuitarReemplazante}
            onMatchSustituto={handleMatchSustituto}
            onQuitarSustituto={handleQuitarSustituto}
          />
        )}
      </Modal>
    </>
  )
}

export default BuscarPorTextoTab
